import Phaser from 'phaser';
import { petAssetUrl } from '@/assets/paths';
import { EGGS_BY_ID } from '@/config/eggs.config';
import { resolveHatchPreset, type HatchRarityPreset } from '@/config/hatchPresets.config';
import {
  variantScaleMult,
  applyVariantFx,
  destroyVariantFx,
  type VariantFxOwner,
  type VariantFxRefs,
} from '@/game/fx/variantFx';
import type { PetDef } from '@/domain/types/pet';
import type { Variant, ID } from '@/domain/types/common';

/**
 * HatchDirector — orkiestrator hatch cinematic w Phaser (sesja 29).
 *
 * **Architektura:**
 *  - `HatchScene` (lifecycle owner) tworzy 1× HatchDirector w `create()`.
 *  - Director trzyma WSZYSTKIE tymczasowe obiekty Phaser (sprite'y, tweeny,
 *    timery, emittery, graphics) w internal arrays/refs.
 *  - `play()` zwraca Promise który resolveje gdy animacja kończy się
 *    naturalnie LUB przez `skip()`.
 *  - `destroy()` jest **single point of cleanup** — idempotent.
 *
 * **Sesja 29B (rarity + variant FX):**
 *  - Preset per rarity z `hatchPresets.config.ts`:
 *    - kolory flash/shockwave/particles
 *    - particle count
 *    - flash/shockwave radius
 *    - shockwave ring count (1 lub 2 dla legendary/mythic)
 *    - camera shake intensity + duration
 *    - duration multiplier (legendary trwa ~25% dłużej)
 *  - HUGE override (`HUGE_HATCH_PRESET`) niezależny od rarity.
 *  - **Variant FX** w reveal phase przez `applyVariantFx` z `variantFx.ts`
 *    (DRY z Pet.ts):
 *    - gold: tint + shimmer flash co 2.5s + subtle aura
 *    - rainbow: animowany 4-corner gradient tint
 *    - huge: scale ×1.45 + biało-złoty aura
 *
 * **Skip:** dostępny po `SKIP_AVAILABLE_AFTER_MS = 400`. Skip czyści tweens/
 * timers/emitters przez `destroy()` — żaden zombie nie zostaje.
 */

export interface HatchPayload {
  petDef: PetDef;
  variant: Variant;
  eggDefId: ID;
  isHuge: boolean;
  power: number;
}

/** Bazowe czasy faz w ms — przed durationMult preset'u. */
const BASE_PHASE_DURATIONS = {
  intro: 350,
  charge: 750,    // 350 → 1100
  burst: 300,     // 1100 → 1400
  reveal: 600,    // 1400 → 2000
} as const;

const BASE_TOTAL_DURATION_MS =
  BASE_PHASE_DURATIONS.intro + BASE_PHASE_DURATIONS.charge
  + BASE_PHASE_DURATIONS.burst + BASE_PHASE_DURATIONS.reveal;

/** Po ilu ms od startu skip jest dostępny (mniej niż dokument 500ms — refinement). */
export const SKIP_AVAILABLE_AFTER_MS = 400;

export class HatchDirector {
  private readonly tweens: Phaser.Tweens.Tween[] = [];
  private readonly timers: Phaser.Time.TimerEvent[] = [];
  private readonly emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  /** Sprite'y/Graphics/Container do destroy. Container z `destroy(true)` zniszczy też swoje dzieci. */
  private readonly displayObjects: Phaser.GameObjects.GameObject[] = [];

  /** Variant FX refs (aura + tweens dla gold shimmer / rainbow tint) — sesja 29B. */
  private variantFxRefs: VariantFxRefs | null = null;
  /** Owner dla variant tween properties (rainbowHue, goldShimmerProgress). */
  private readonly variantOwner: VariantFxOwner = {
    goldShimmerProgress: 0,
    rainbowHue: 0,
  };

  /** Preset per rarity + huge override — wybrany w konstruktorze. */
  private readonly preset: HatchRarityPreset;
  /** Czasy faz przeskalowane przez `preset.durationMult` — pre-computed w konstruktorze. */
  private readonly phaseDurations: { intro: number; charge: number; burst: number; reveal: number };
  private readonly totalDurationMs: number;

  private completed = false;
  private skipRequested = false;
  /** Promise chain — resolveje gdy `play()` kończy się naturalnie LUB przez skip. */
  private resolvePlay: (() => void) | null = null;

  private readonly startedAtMs: number;
  /**
   * Wariant "rozmiarowy" sterujący presetem + skalą reveal. Normalizuje legacy
   * flagę `isHuge` (gdy true, traktujemy jak 'huge'). Titanic > huge > reszta.
   */
  private readonly effectiveVariant: Variant;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly payload: HatchPayload,
  ) {
    this.startedAtMs = scene.time.now;
    this.effectiveVariant =
      payload.variant === 'titanic'
        ? 'titanic'
        : payload.isHuge || payload.variant === 'huge'
          ? 'huge'
          : payload.variant;
    this.preset = resolveHatchPreset(payload.petDef.rarity, this.effectiveVariant);
    // Scale phase times by preset.durationMult — legendary trwa dłużej.
    const m = this.preset.durationMult;
    this.phaseDurations = {
      intro: Math.round(BASE_PHASE_DURATIONS.intro * m),
      charge: Math.round(BASE_PHASE_DURATIONS.charge * m),
      burst: Math.round(BASE_PHASE_DURATIONS.burst * m),
      reveal: Math.round(BASE_PHASE_DURATIONS.reveal * m),
    };
    this.totalDurationMs = Math.round(BASE_TOTAL_DURATION_MS * m);
  }

  /**
   * Odpala cinematic. Promise resolveje gdy animacja kończy się naturalnie
   * (lub przez `skip()`). `destroy()` jest wołane PRZED resolve żeby cleanup
   * był gwarantowany dla każdego ścieżki wyjścia.
   */
  play(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resolvePlay = resolve;

      // Centralny container — wszystkie elementy hatcha są jego dziećmi.
      // `destroy(true)` na containerze niszczy też dzieci.
      const cam = this.scene.cameras.main;
      const centerX = cam.scrollX + cam.width / 2;
      const centerY = cam.scrollY + cam.height / 2;

      const container = this.scene.add.container(centerX, centerY);
      container.setDepth(1000);   // Nad wszystkim w HatchScene
      this.displayObjects.push(container);

      // ── Phase 1: intro — dim + egg pojawia się ─────────────────────────
      // Półprzezroczyste tło (rectangle pełnoekranowy, fade in z 0 → 0.7).
      // Liczone w world coords HatchScene (camera scrolls są 0 dla overlay scene).
      const dim = this.scene.add.rectangle(0, 0, cam.width, cam.height, 0x000000, 0);
      dim.setOrigin(0, 0);
      dim.setScrollFactor(0);
      dim.setDepth(999);
      this.displayObjects.push(dim);

      this.addTween({
        targets: dim,
        alpha: 0.7,
        duration: this.phaseDurations.intro,
        ease: 'Quad.easeOut',
      });

      // Egg sprite — start scale 0, anim do `eggScaleTarget` w fazie intro.
      // Sesja 29A review HIGH: sprite source jest 1024×1024 (nie 256), więc
      // hardcoded /256 dawał egg ×4 zalewający viewport. Liczymy target z
      // RZECZYWISTEGO rozmiaru tekstury — dłuższa krawędź ma być ~40% mniejszego
      // wymiaru viewportu (czyt. ale nie dominujące).
      const eggDef = EGGS_BY_ID[this.payload.eggDefId];
      const eggTextureKey = eggDef?.art.textureKey ?? 'egg_basic';
      const eggSpriteAvailable = this.scene.textures.exists(eggTextureKey);

      let egg: Phaser.GameObjects.Image | null = null;
      let eggNativeSize: number;   // dłuższa krawędź sprite'a w px (do skalowania)
      if (eggSpriteAvailable) {
        egg = this.scene.add.image(0, 0, eggTextureKey);
        egg.setScale(0.0);   // start invisible, anim w fazie 1
        container.add(egg);
        eggNativeSize = Math.max(egg.width, egg.height);
      } else {
        // Fallback: większy circle z kolorem glowColor. 160px diameter (= radius 80).
        const glowColor = parseHexColor(eggDef?.art.glowColor) ?? 0xffd24a;
        const circle = this.scene.add.circle(0, 0, 80, glowColor, 0.85);
        circle.setStrokeStyle(4, 0x000000);
        circle.setScale(0.0);
        container.add(circle);
        egg = circle as unknown as Phaser.GameObjects.Image;   // type bridging — używamy tylko tween properties
        eggNativeSize = 160;   // circle radius 80 → bounding 160
      }

      const targetEggDisplayPx = Math.min(cam.width, cam.height) * 0.40;
      const eggScaleTarget = targetEggDisplayPx / Math.max(1, eggNativeSize);

      this.addTween({
        targets: egg,
        scale: eggScaleTarget,
        duration: this.phaseDurations.intro,
        ease: 'Back.easeOut',
      });

      // ── Phase 2: charge — 3 pulse'y + rotation oscillation ─────────────
      // Triggerowane przez delayed call żeby phases były separate.
      this.addTimer(this.phaseDurations.intro, () => this.playChargePhase(egg, eggScaleTarget));

      // ── Phase 3: burst — wykonywany przez delayed call ─────────────────
      // Sesja 29B: kolor burst NIE jest już brany z eggDef.art.glowColor
      // (egg-themed), tylko z preset (rarity-themed). Gracz powinien widzieć
      // rarity nim sprite peta się pojawi — toto fioletowy burst = epic itd.
      this.addTimer(this.phaseDurations.intro + this.phaseDurations.charge, () =>
        this.playBurstPhase(egg, container),
      );

      // ── Phase 4: reveal — pet sprite z bounce ─────────────────────────
      this.addTimer(
        this.phaseDurations.intro + this.phaseDurations.charge + this.phaseDurations.burst,
        () => this.playRevealPhase(container),
      );

      // ── End: complete naturalnie ──────────────────────────────────────
      this.addTimer(this.totalDurationMs, () => this.complete());
    });
  }

  /** Phase 2 — 3 pulse'y + rotation oscillation. */
  private playChargePhase(egg: Phaser.GameObjects.Image | null, baseScale: number): void {
    if (!egg || this.completed) return;

    // 3 pulse'y: amplituda rośnie (1.0 / 1.05 / 1.1)
    // Każdy pulse: scale up + scale down, łącznie 750ms / 3 = 250ms per pulse
    const pulseDuration = this.phaseDurations.charge / 3;

    this.addTween({
      targets: egg,
      scale: { from: baseScale, to: baseScale * 1.10 },
      duration: pulseDuration / 2,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });

    // Rotation oscillation: -5° → +5° → -8° → +8°, asymetryczny żeby nie wyglądało robotycznie
    this.addTween({
      targets: egg,
      angle: { from: -5, to: 5 },
      duration: pulseDuration / 2,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Phase 3 — burst: scale 1.1 → 1.35 → 0, flash, shockwave ring, particles.
   * Sesja 29B: używa `this.preset` (per-rarity) zamiast hardcoded — kolory,
   * particle count, radius, ring count, camera shake.
   */
  private playBurstPhase(
    egg: Phaser.GameObjects.Image | null,
    container: Phaser.GameObjects.Container,
  ): void {
    if (!egg || this.completed) return;

    const preset = this.preset;

    // Egg quick scale-up then disappear
    this.addTween({
      targets: egg,
      scale: '+=0.25',
      duration: this.phaseDurations.burst * 0.4,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        if (this.completed) return;
        this.addTween({
          targets: egg,
          scale: 0,
          alpha: 0,
          duration: this.phaseDurations.burst * 0.4,
          ease: 'Cubic.easeIn',
        });
      },
    });

    // Flash — kolor preset, alpha preset, rośnie do preset.flashRadius
    const flash = this.scene.add.circle(0, 0, 30, preset.primaryColor, preset.flashAlpha);
    flash.setBlendMode(Phaser.BlendModes.ADD);
    container.add(flash);

    this.addTween({
      targets: flash,
      radius: preset.flashRadius,
      alpha: 0,
      duration: this.phaseDurations.burst,
      ease: 'Cubic.easeOut',
    });

    // Shockwave ringi — 1 lub 2 (legendary/mythic dostają 2 z opóźnieniem).
    this.spawnShockwaveRing(container, preset.primaryColor, preset, 0);
    if (preset.shockwaveRingCount === 2) {
      // Drugi ring startuje z opóźnieniem 80ms — daje "echo" feel.
      this.spawnShockwaveRing(container, preset.secondaryColor, preset, 80);
    }

    // Container shake (sesja 29B review fix MEDIUM A): zamiast `camera.shake`,
    // trzęsiemy tylko hatch container — pet/flash/ring/particles wszystkie
    // się ruszają razem, ale dim (poza containerem, scrollFactor=0) pozostaje
    // stabilny. Camera shake odsłaniał cienkie nieprzyciemnione krawędzie
    // ekranu przy rare+ intensywności bo dim ma dokładnie rozmiar viewportu.
    if (preset.cameraShakeIntensity > 0) {
      this.playContainerShake(container, preset.cameraShakeIntensity, preset.cameraShakeMs);
    }

    // Radial particles — kolor + count z preset
    if (this.scene.textures.exists(preset.particleTextureKey)) {
      const particles = this.scene.add.particles(0, 0, preset.particleTextureKey, {
        speed: { min: 120, max: 260 },
        lifespan: 700,
        scale: { start: 1.0, end: 0 },
        angle: { min: 0, max: 360 },
        rotate: { min: 0, max: 360 },
        tint: preset.primaryColor,   // re-tint particle żeby pasował rarity
        quantity: preset.particleCount,
        gravityY: 100,
        emitting: false,
      });
      particles.setDepth(1001);
      container.add(particles);
      particles.explode(preset.particleCount, 0, 0);
      this.emitters.push(particles);
    }
  }

  /**
   * Spawnuje shockwave ring (rosnący okrąg z fade-out alpha). Wydzielone bo
   * legendary/mythic dostają 2 ringi z opóźnieniem dla "echo" feel.
   */
  private spawnShockwaveRing(
    container: Phaser.GameObjects.Container,
    color: number,
    preset: HatchRarityPreset,
    delayMs: number,
  ): void {
    const ring = this.scene.add.graphics();
    ring.setBlendMode(Phaser.BlendModes.ADD);
    container.add(ring);

    const ringDuration = this.phaseDurations.burst + 100;
    const ringMaxR = preset.shockwaveRadius;
    const ringLW = preset.shockwaveLineWidth;

    // Delay przez addTimer — zapisany w this.timers do cleanup.
    this.addTimer(delayMs, () => {
      if (this.completed) return;
      const ringStart = this.scene.time.now;
      const ringTimer = this.scene.time.addEvent({
        delay: 16,
        loop: true,
        callback: () => {
          if (this.completed) {
            ringTimer.remove();
            return;
          }
          const elapsed = this.scene.time.now - ringStart;
          const t = Math.min(1, elapsed / ringDuration);
          const r = ringMaxR * t;
          const alpha = (1 - t) * 0.85;
          ring.clear();
          ring.lineStyle(ringLW, color, alpha);
          ring.strokeCircle(0, 0, r);
          if (t >= 1) ringTimer.remove();
        },
      });
      this.timers.push(ringTimer);
    });
  }

  /**
   * Container shake z decay — sesja 29B review fix MEDIUM A.
   *
   * Custom implementacja (Phaser nie ma builtin shake na Container/GameObject).
   * TimerEvent loop modyfikuje `container.x/y` co 30ms o losowy offset.
   * Amplituda słabnie liniowo do 0 (decay) — naturalne wygasanie wstrząsu.
   *
   * **Dlaczego nie `cameras.main.shake`:** camera shake wpływa na cały
   * rendering sceny, w tym dim rectangle (poza containerem). Dim ma rozmiar
   * = viewport, więc shake odsłania nieprzyciemnione krawędzie ekranu.
   * Container shake trzęsie tylko zawartością hatcha (pet/flash/ring/particles
   * /aura), dim zostaje stabilny.
   *
   * **intensityRel:** taki sam format jak Phaser camera.shake — wartość względna
   * do max wymiaru viewportu (0.015 = 1.5% × max(width, height)).
   */
  private playContainerShake(
    container: Phaser.GameObjects.Container,
    intensityRel: number,
    durationMs: number,
  ): void {
    const cam = this.scene.cameras.main;
    const maxOffsetPx = Math.max(cam.width, cam.height) * intensityRel;
    const originalX = container.x;
    const originalY = container.y;
    const startMs = this.scene.time.now;

    const shakeTimer = this.scene.time.addEvent({
      delay: 30,
      loop: true,
      callback: () => {
        // Defensive: jeśli completed mid-shake, zatrzymaj i wróć do origin.
        if (this.completed) {
          container.setPosition(originalX, originalY);
          shakeTimer.remove();
          return;
        }
        const elapsed = this.scene.time.now - startMs;
        if (elapsed >= durationMs) {
          container.setPosition(originalX, originalY);
          shakeTimer.remove();
          return;
        }
        // Linear decay: amplituda słabnie z czasem (na końcu = 0).
        const decay = 1 - (elapsed / durationMs);
        const offsetX = (Math.random() - 0.5) * 2 * maxOffsetPx * decay;
        const offsetY = (Math.random() - 0.5) * 2 * maxOffsetPx * decay;
        container.setPosition(originalX + offsetX, originalY + offsetY);
      },
    });
    this.timers.push(shakeTimer);
  }

  /** Phase 4 — reveal: pet sprite z bounce easing + variant FX (sesja 29B). */
  private playRevealPhase(container: Phaser.GameObjects.Container): void {
    if (this.completed) return;

    const { petDef } = this.payload;
    const biomeId = petDef.origin?.biomeId ?? 'biome-meadow';
    const petTextureKey = petDef.art.textureKey;
    const petSpriteAvailable = this.scene.textures.exists(petTextureKey);

    if (!petSpriteAvailable) {
      console.warn(`[HatchDirector] Brak tekstury peta '${petTextureKey}', reveal pomija sprite`);
      return;
    }

    // Sesja 29A review HIGH: liczymy target z RZECZYWISTEGO rozmiaru sprite'a.
    // Pety są typowo 1024×1024 (źle gdy /256). Target: dłuższa krawędź =
    // ~45% mniejszego wymiaru viewportu (większe od egga w fazie reveal,
    // bo to focal point).
    // Sesja 29B: huge variant dostaje × HUGE_SCALE_MULT (1.45) — wizualnie
    // wyraźnie większy, jak w runtime Pet (Codex 23d: huge = masa).
    const cam = this.scene.cameras.main;
    const pet = this.scene.add.image(0, 0, petTextureKey);
    pet.setScale(0);
    container.add(pet);

    const petNativeSize = Math.max(pet.width, pet.height);
    const isBigVariant = this.effectiveVariant === 'huge' || this.effectiveVariant === 'titanic';
    const sizeMult = variantScaleMult(this.effectiveVariant);
    const targetPetDisplayPx = Math.min(cam.width, cam.height) * 0.45;
    const petTargetScale = (targetPetDisplayPx / Math.max(1, petNativeSize)) * sizeMult;

    // Bounce: 0 → 1.25 → 1.0 z Back.easeOut. Huge/titanic mają wolniejszy reveal —
    // dłuższa pierwsza faza (0.7 vs 0.6 ratio) dla "wow ciężaru".
    const bouncePeak = petTargetScale * 1.25;
    const bouncePhase1Ratio = isBigVariant ? 0.7 : 0.6;
    this.addTween({
      targets: pet,
      scale: bouncePeak,
      duration: this.phaseDurations.reveal * bouncePhase1Ratio,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (this.completed) return;
        this.addTween({
          targets: pet,
          scale: petTargetScale,
          duration: this.phaseDurations.reveal * (1 - bouncePhase1Ratio),
          ease: 'Quad.easeOut',
        });
      },
    });

    // ── Variant FX (sesja 29B) — gold/rainbow/huge widoczne już w reveal ──
    // Wspólny kod z Pet.ts przez `applyVariantFx` z variantFx.ts (DRY).
    // Aura position = (0, 0) bo pet jest w containerze ze środkiem ekranu.
    //
    // **Aura radius scaling (sesja 29B review fix MEDIUM B):** default
    // `AURA_RADIUS = 28px` jest dobry dla peta 48px w runtime, ale w hatch
    // reveal pet jest ~petDisplayPx (np. 162px na mobile / 235px huge na mobile).
    // Aura 28px byłaby schowana pod sprite'em. Liczymy radius proporcjonalnie:
    //   - 65% display = aura WIDOCZNIE większa od peta (~ +30% z każdej strony)
    //   - sizeMult już zawiera huge ×1.45, więc aura też proporcjonalnie większa
    const petDisplayPx = petTargetScale * petNativeSize;
    const hatchAuraRadius = petDisplayPx * 0.65;
    this.variantFxRefs = applyVariantFx(
      this.scene,
      pet,
      this.payload.variant,
      this.variantOwner,
      { x: 0, y: 0 },
      hatchAuraRadius,
    );
    // Aura graphics powstał w `applyVariantFx` jako bezpośredni child sceny,
    // NIE containera — pozycja w world coords. Trzeba ją sparent'ować do
    // containera żeby (a) była nad dim'em (depth >= container 1000),
    // (b) była czyszczona z container.destroy(true).
    if (this.variantFxRefs.aura) {
      const aura = this.variantFxRefs.aura;
      // Center containera = środek ekranu, więc (0, 0) w container = pet pos.
      aura.setPosition(0, 0);
      container.add(aura);
      // Set depth wewnątrz containera tak, żeby aura była POD pet sprite'em.
      // Container automatycznie utrzymuje render order, ale ustawiamy explicit
      // przez moveDown — aura była ostatnio dodana, więc jest na top.
      container.moveDown(aura);
    }

    void petAssetUrl(petTextureKey, biomeId);   // path helper reference (no-op call)
  }

  /**
   * Skip — natychmiast zakończ cinematic. Cleanup, resolve play(). Idempotent.
   *
   * Po skipie React od razu pokazuje finalny panel — gracz nie czeka.
   */
  skip(): void {
    if (this.completed || this.skipRequested) return;
    // Skip jest dostępny dopiero po SKIP_AVAILABLE_AFTER_MS — bez tego, click
    // przed startem animacji by ją przerwał zanim cokolwiek się pokaże.
    const elapsedMs = this.scene.time.now - this.startedAtMs;
    if (elapsedMs < SKIP_AVAILABLE_AFTER_MS) return;
    this.skipRequested = true;
    this.complete();
  }

  /**
   * Zakończ cinematic — naturalnie LUB przez skip. Idempotent.
   * Robi cleanup + resolve play(). Nie emituje `hatch:complete` — to robi
   * HatchScene po wywołaniu tej funkcji (event jest separation of concerns).
   */
  private complete(): void {
    if (this.completed) return;
    this.completed = true;
    this.destroy();
    this.resolvePlay?.();
    this.resolvePlay = null;
  }

  /**
   * Single point of cleanup — niszczy wszystkie tweeny, timery, emittery,
   * sprite'y. Wołane przez `complete()` i bezpośrednio przez HatchScene
   * w SHUTDOWN handler (defensive). Idempotent.
   */
  destroy(): void {
    for (const tween of this.tweens) {
      tween.remove();
    }
    this.tweens.length = 0;

    for (const timer of this.timers) {
      timer.remove();
    }
    this.timers.length = 0;

    for (const emitter of this.emitters) {
      emitter.destroy();
    }
    this.emitters.length = 0;

    // Sesja 29B: variant FX cleanup PRZED container.destroy(true) — bez tego
    // tween na sprite'cie pet'a próbowałby modyfikować destroyed Image.
    // `destroyVariantFx` woła `tween.remove()` + `aura.destroy()`. Aura jest
    // dzieckiem containera więc destroy(true) by ją nuke'ował, ale destroyVariantFx
    // robi to explicit + zeruje references.
    if (this.variantFxRefs) {
      destroyVariantFx(this.variantFxRefs);
      this.variantFxRefs = null;
    }

    for (const obj of this.displayObjects) {
      if ('destroy' in obj && typeof obj.destroy === 'function') {
        obj.destroy(true);   // true = destroy children dla Container
      }
    }
    this.displayObjects.length = 0;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Rejestruje tween w `this.tweens` żeby był sprzątnięty przy cleanup'cie. */
  private addTween(config: Phaser.Types.Tweens.TweenBuilderConfig): Phaser.Tweens.Tween {
    const tween = this.scene.tweens.add(config);
    this.tweens.push(tween);
    return tween;
  }

  /** Rejestruje delayed call w `this.timers` — używamy `TimerEvent` (nie pojedynczego setTimeout). */
  private addTimer(delayMs: number, callback: () => void): Phaser.Time.TimerEvent {
    const timer = this.scene.time.delayedCall(delayMs, callback);
    this.timers.push(timer);
    return timer;
  }
}

// ── Module-level helpers ──────────────────────────────────────────────────

/**
 * Parsuje hex color string (`#FFD24A` lub `0xFFD24A`) → number dla Phaser API.
 * Zwraca undefined gdy null/undefined/invalid.
 */
function parseHexColor(s: string | undefined): number | undefined {
  if (!s) return undefined;
  const trimmed = s.trim();
  const hex = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed.startsWith('0x') ? trimmed.slice(2) : trimmed;
  const n = parseInt(hex, 16);
  return isNaN(n) ? undefined : n;
}

// `HATCH_TOTAL_DURATION_MS` był używany w sesji 29A jako static export. Teraz
// total duration jest per-rarity (preset.durationMult), więc liczone w instance.
// Caller (HatchScene) i tak czeka na Promise resolve z `play()` — nie potrzebuje
// stałej. Eksport zostawiamy jako bazowy "common" total (1.0× durationMult).
export const HATCH_BASE_TOTAL_DURATION_MS = BASE_TOTAL_DURATION_MS;
